import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Mail, Phone, Clock, ArrowUpRight, ArrowDownLeft, Send } from 'lucide-react';
import communicationService from '../../services/communicationService';
import NewMessageModal from './NewMessageModal';

const TYPE_CONFIG = {
  sms: { icon: Phone, label: 'SMS', color: 'text-green-600', bg: 'bg-green-50' },
  email: { icon: Mail, label: 'Email', color: 'text-blue-600', bg: 'bg-blue-50' },
  portal: { icon: MessageSquare, label: 'Portal', color: 'text-purple-600', bg: 'bg-purple-50' },
};

const PAGE_SIZE = 100;
const SCROLL_LOAD_THRESHOLD_PX = 200;

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Renders the inline communications section on a Client or Case detail page.
// Shows all applicable communications in a scrollable container, sorted most
// recent first, with lazy-loaded pagination on scroll. Pass exactly one of
// caseId or clientId.
const CaseCommunicationsSection = ({ caseId, clientId }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const scrollContainerRef = useRef(null);

  const buildFilter = useCallback((pageNum) => {
    const params = { page: pageNum, size: PAGE_SIZE };
    if (caseId) params.case_id = caseId;
    if (clientId) params.client_id = clientId;
    return params;
  }, [caseId, clientId]);

  const fetchPage = useCallback(async (pageNum, append) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      const response = await communicationService.getCommunications(buildFilter(pageNum));
      const items = response.communications || [];
      setCommunications(prev => append ? [...prev, ...items] : items);
      setTotalCount(response.total || 0);
      setHasMore(Boolean(response.has_more));
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching communications:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildFilter]);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const handleScroll = useCallback((e) => {
    if (loadingMore || !hasMore) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
      fetchPage(page + 1, true);
    }
  }, [loadingMore, hasMore, page, fetchPage]);

  const handleMessageSent = () => {
    setShowNewMessage(false);
    fetchPage(1, false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold flex items-center mb-4">
          <MessageSquare className="mr-2 h-5 w-5" />
          Communications
        </h2>
        <p className="text-gray-400 text-sm">Loading communications...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center">
          <MessageSquare className="mr-2 h-5 w-5" />
          Communications
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">({totalCount})</span>
          )}
        </h2>
        <button
          onClick={() => setShowNewMessage(true)}
          className="bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center hover:bg-green-700"
        >
          <Send className="mr-1 h-3.5 w-3.5" />
          New Message
        </button>
      </div>

      {communications.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600">No communications yet</p>
          <p className="text-gray-500 text-sm">Send a message to get started</p>
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="space-y-2 max-h-[600px] overflow-y-auto pr-1"
        >
          {communications.map((comm) => {
            const typeConfig = TYPE_CONFIG[comm.type] || TYPE_CONFIG.email;
            const TypeIcon = typeConfig.icon;
            const isInbound = comm.direction === 'inbound';
            const DirectionIcon = isInbound ? ArrowDownLeft : ArrowUpRight;
            const senderName = isInbound
              ? (comm.client_name || comm.from_address || 'Client')
              : (comm.user_name || 'You');
            const preview = comm.content
              ? comm.content.length > 100 ? comm.content.substring(0, 100) + '...' : comm.content
              : comm.subject || 'No content';

            return (
              <div
                key={comm.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-1.5 rounded-md ${typeConfig.bg} flex-shrink-0 mt-0.5`}>
                    <TypeIcon className={`h-3.5 w-3.5 ${typeConfig.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <DirectionIcon className={`h-3 w-3 ${isInbound ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {senderName}
                        </span>
                        {comm.subject && (
                          <span className="text-xs text-gray-500 truncate hidden sm:inline">
                            — {comm.subject}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0 ml-2">
                        <Clock className="h-3 w-3" />
                        {formatTime(comm.created_at)}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{preview}</p>
                  </div>
                </div>
              </div>
            );
          })}
          {loadingMore && (
            <p className="text-center text-xs text-gray-400 py-2">Loading more…</p>
          )}
          {!hasMore && communications.length > PAGE_SIZE && (
            <p className="text-center text-xs text-gray-400 py-2">End of communications</p>
          )}
        </div>
      )}

      {showNewMessage && (
        <NewMessageModal
          onClose={() => setShowNewMessage(false)}
          onMessageSent={handleMessageSent}
          preSelectedClientId={clientId}
          preSelectedCaseId={caseId}
        />
      )}
    </div>
  );
};

export default CaseCommunicationsSection;
